// Unified admin UI style system tokens.
// Keep legacy flat keys for backward compatibility while exposing semantic groups.

const layout = {
  container: 'mx-auto w-full',
  page: 'w-full space-y-4',
  pageWrapper: 'w-full space-y-4',
  pageHeader: 'flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between',
  pageTitle: 'text-2xl font-semibold text-foreground',
  pageSubtitle: 'text-sm text-muted-foreground',
  section: 'space-y-4',
  sectionWrapper: 'space-y-4',
  contentGrid: 'grid gap-4 sm:grid-cols-2 xl:grid-cols-4',
  responsiveContainer: 'mx-auto w-full max-w-7xl',
}

const cards = {
  base: 'rounded-xl border border-outline bg-surface text-foreground shadow-sm',
  default: 'rounded-xl border border-outline bg-surface text-foreground shadow-sm',
  compact: 'rounded-lg border border-outline bg-surface text-foreground shadow-sm p-3',
  highlighted: 'rounded-xl border border-primary/30 bg-surface-elevated text-foreground shadow-sm',
  warning: 'rounded-xl border border-warning/40 bg-warning/10 text-foreground shadow-sm',
  danger: 'rounded-xl border border-danger/40 bg-danger/10 text-foreground shadow-sm',
  header: 'p-6 border-b border-divider font-semibold text-foreground',
  body: 'p-6 text-foreground',
}

const buttonBase = 'inline-flex items-center justify-center gap-2 rounded-md text-sm font-medium transition-colors duration-150 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-background disabled:cursor-not-allowed disabled:opacity-50'

const buttons = {
  base: buttonBase,
  primary: `${buttonBase} bg-primary text-primary-foreground hover:bg-primary-hover focus:ring-primary`,
  secondary: `${buttonBase} border border-outline bg-surface text-foreground hover:bg-surface-elevated focus:ring-primary`,
  ghost: `${buttonBase} bg-surface-elevated text-foreground hover:opacity-90 focus:ring-primary`,
  danger: `${buttonBase} bg-danger text-white hover:opacity-90 focus:ring-danger`,
  success: `${buttonBase} bg-success text-primary-text hover:opacity-90 focus:ring-success`,
  icon: `${buttonBase} h-9 w-9 p-0 text-muted-foreground hover:bg-surface-elevated hover:text-foreground focus:ring-primary`,
  sm: 'px-3 py-1.5 text-xs',
  md: 'px-4 py-2 text-sm',
  lg: 'px-5 py-2.5 text-base',
  smPrimary: `${buttonBase} bg-primary px-2 py-1 text-sm text-primary-foreground hover:bg-primary-hover disabled:opacity-50`,
  smSuccess: `${buttonBase} bg-success px-2 py-1 text-sm text-primary-text hover:opacity-90 disabled:opacity-50`,
  smDanger: `${buttonBase} bg-danger px-2 py-1 text-sm text-white hover:opacity-90 disabled:opacity-50`,
  smGhost: `${buttonBase} border border-outline bg-surface-elevated px-2 py-1 text-sm text-foreground hover:bg-surface disabled:opacity-50`,
}

const forms = {
  label: 'block text-sm font-medium text-foreground',
  input: 'block w-full rounded-md border border-outline bg-surface px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground shadow-sm focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/30',
  inputSm: 'rounded-md border border-outline bg-surface px-2 py-1 text-sm text-foreground placeholder:text-muted-foreground',
  textarea: 'block min-h-28 w-full rounded-md border border-outline bg-surface px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground shadow-sm focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/30',
  select: 'block w-full rounded-md border border-outline bg-surface py-2 px-3 text-foreground shadow-sm focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/30 sm:text-sm',
  checkbox: 'h-4 w-4 rounded border-outline bg-surface text-primary focus:ring-primary',
  fieldError: 'mt-1 text-sm text-danger',
  helpText: 'mt-1 text-xs text-muted-foreground',
  actions: 'mt-4 flex flex-wrap items-center gap-2',
}

const tables = {
  wrapper: 'rounded-xl border border-outline bg-surface shadow-sm',
  table: 'min-w-full text-left text-foreground',
  head: 'bg-table-header',
  headCell: 'px-6 py-3 text-xs font-medium text-muted-foreground uppercase tracking-wider',
  body: 'divide-y divide-divider',
  row: 'bg-table-row text-foreground',
  rowHover: 'hover:bg-table-row-hover',
  rowSelected: 'bg-surface-elevated',
  cell: 'px-6 py-4 whitespace-nowrap',
  cellText: 'px-6 py-4 whitespace-nowrap text-sm text-foreground',
  actionCell: 'px-6 py-4 whitespace-nowrap text-right text-sm font-medium text-foreground',
  emptyRow: 'px-6 py-8 text-center text-sm text-muted-foreground',
}

const states = {
  loading: 'rounded-lg border border-outline bg-surface-elevated p-4 text-sm text-foreground',
  empty: 'rounded-lg border border-dashed border-outline bg-surface p-8 text-center text-sm text-muted-foreground',
  error: 'rounded-lg border border-danger/40 bg-danger/10 p-4 text-sm text-danger',
  success: 'rounded-lg border border-success/40 bg-success/10 p-4 text-sm text-foreground',
  warning: 'rounded-lg border border-warning/40 bg-warning/10 p-4 text-sm text-foreground',
}

const badges = {
  base: 'inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ring-1 ring-inset',
  success: 'bg-success/20 text-foreground ring-success/40',
  warning: 'bg-warning/20 text-foreground ring-warning/40',
  danger: 'bg-danger/20 text-foreground ring-danger/40',
  neutral: 'bg-surface-elevated text-foreground ring-outline',
  info: 'bg-primary/20 text-foreground ring-primary/40',
}

const feedback = {
  alertInfo: 'rounded-lg border border-outline bg-surface-elevated p-4 text-sm text-foreground',
  alertError: 'rounded-lg border border-danger/40 bg-danger/10 p-4 text-sm text-danger',
  spinner: 'animate-spin rounded-full h-8 w-8 border-b-2 border-primary',
  spinnerLg: 'animate-spin rounded-full h-12 w-12 border-b-2 border-primary',
}

const actionBar = {
  base: 'flex items-center justify-between gap-4 p-4 bg-surface-elevated border-b border-divider',
  actions: 'flex items-center gap-2',
}

const sectionCard = {
  base: 'rounded-lg border border-outline bg-surface text-foreground shadow-sm p-4',
  header: 'font-semibold text-foreground mb-2',
  body: 'text-sm text-muted-foreground',
}

const pageShell = {
  base: 'flex min-h-screen flex-col bg-background text-foreground',
  header: 'border-b border-divider bg-surface p-4 shadow-sm',
  content: 'flex-1 p-6',
}

const pageHeader = {
  base: 'flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between',
  title: 'text-2xl font-semibold text-foreground',
  subtitle: 'text-sm text-muted-foreground',
}

export const ui = {
  layout,
  cards,
  buttons,
  forms,
  tables,
  states,
  badges,
  feedback,

  // Legacy aliases used by existing pages.
  page: layout.page,
  section: layout.section,
  card: cards.base,
  cardHeader: cards.header,
  cardBody: cards.body,
  title: layout.pageTitle,
  subtitle: layout.pageSubtitle,
  label: forms.label,

  buttonPrimary: `${buttons.primary} ${buttons.md}`,
  buttonSuccess: `${buttons.success} ${buttons.md}`,
  buttonDanger: `${buttons.danger} ${buttons.md}`,
  buttonGhost: `${buttons.ghost} ${buttons.md}`,
  buttonSmPrimary: buttons.smPrimary,
  buttonSmSuccess: buttons.smSuccess,
  buttonSmDanger: buttons.smDanger,
  buttonSmGhost: buttons.smGhost,
  buttonIcon: buttons.icon,

  input: forms.input,
  inputSm: forms.inputSm,
  select: forms.select,

  table: tables.table,
  tableHead: tables.head,
  tableHeadCell: tables.headCell,
  tableRow: `${tables.row} ${tables.rowHover}`,
  tableRowSelected: tables.rowSelected,
  tableCell: tables.cell,
  tableCellText: tables.cellText,

  badge: badges.base,
  badgeSuccess: `${badges.base} ${badges.success}`,
  badgeWarning: `${badges.base} ${badges.warning}`,
  badgeError: `${badges.base} ${badges.danger}`,
  badgeNeutral: `${badges.base} ${badges.neutral}`,

  alertInfo: feedback.alertInfo,
  alertError: feedback.alertError,
  spinner: feedback.spinner,
  spinnerLg: feedback.spinnerLg,

  actionBar,
  sectionCard,
  pageShell,
  pageHeader,
}
